import React, { useState, useEffect, useCallback } from 'react';
import { Filter, Download, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_BASE_URL } from "../config/apiconfig";
import { ui } from "../config/theme";


const CandidatesPage = () => {
    const [candidates, setCandidates] = useState([]);
    const [filteredCandidates, setFilteredCandidates] = useState([]);
    const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
    const [filters, setFilters] = useState({
        skills: '',
        education: '',
        company: '',
        license: '',
        location: ''
    });

    const validateCandidate = (candidate) => {
        return (
            candidate.firstName &&
            candidate.lastName &&
            candidate.email &&
            candidate.location &&
            Array.isArray(candidate.skills) && 
            candidate.skills.length > 0 &&
            Array.isArray(candidate.education) && 
            candidate.education.length > 0 &&
            Array.isArray(candidate.careers) && 
            candidate.careers.length > 0 &&
            Array.isArray(candidate.licenses) && 
            candidate.licenses.length > 0 && 
            Array.isArray(candidate.languages) &&
            candidate.languages.length > 0
        );
    };

    const fetchCandidates = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/candidates`);
            const data = await response.json();
            const validCandidates = data.filter(validateCandidate);
            setCandidates(validCandidates);
            setFilteredCandidates(validCandidates);
        } catch (error) {
            console.error('Error fetching candidates:', error);
        }
    }, []);

    const filterCandidates = useCallback(() => {
        let filtered = candidates;

        if (filters.skills) {
            filtered = filtered.filter(candidate => 
                candidate.skills.some(skill => 
                    skill.toLowerCase().includes(filters.skills.toLowerCase())
                )
            );
        }

        if (filters.education) {
            filtered = filtered.filter(candidate =>
                candidate.education.some(edu =>
                    edu.degreeProgram.toLowerCase().includes(filters.education.toLowerCase()) ||
                    edu.instituteName.toLowerCase().includes(filters.education.toLowerCase())
                )
            );
        }

        if (filters.company) {
            filtered = filtered.filter(candidate =>
                candidate.careers.some(career =>
                    career.companyName.toLowerCase().includes(filters.company.toLowerCase())
                )
            );
        }

        if (filters.license) {
            filtered = filtered.filter(candidate =>
                candidate.licenses.some(license =>
                    license.name.toLowerCase().includes(filters.license.toLowerCase())
                )
            );
        }

        if (filters.location) {
            filtered = filtered.filter(candidate =>
                candidate.location.toLowerCase().includes(filters.location.toLowerCase())
            );
        }

        setFilteredCandidates(filtered);
    }, [candidates, filters]);

    useEffect(() => {
        fetchCandidates();
    }, [fetchCandidates]);

    useEffect(() => {
        filterCandidates();
    }, [filterCandidates]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCall = (phoneCode, phoneNumber) => {
        if (phoneCode && phoneNumber) {
            window.location.href = `tel:${phoneCode}${phoneNumber}`;
        }
    };

    const downloadResume = async (email) => {
        try {
            const response = await fetch(`${API_BASE_URL}/profile/${email}/resume`);
            
            if (response.status === 404) {
                throw new Error('No resume found for this candidate');
            }
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to download resume');
            }

            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'resume';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            const blob = await response.blob();
            if (blob.size === 0) {
                throw new Error('Downloaded file is empty');
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading resume:', error);
            alert(`Resume download failed: ${error.message}`);
        }
    };

    return (
        <>
                    <div className={ui.page}>
                        <Header />
                        <main className="flex-grow">
                            <div className={ui.container}>
                        <div className="mb-8 space-y-4">
                            <h1 className="text-3xl font-semibold text-gray-900">Candidate Search</h1>

                            <div className="bg-white rounded-lg shadow-md p-6">
                                <div className="flex items-center mb-4">
                                    <button 
                                        onClick={() => setIsAdvancedFilterOpen(!isAdvancedFilterOpen)}
                                        className="flex items-center text-blue-600 hover:text-blue-800"
                                    >
                                        <Filter className="mr-2" /> Advanced Filters
                                    </button>
                                </div>

                                {isAdvancedFilterOpen && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                        <input
                                            type="text"
                                            name="skills"
                                            placeholder="Filter by skills"
                                            value={filters.skills}
                                            onChange={handleFilterChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                        <input
                                            type="text"
                                            name="education"
                                            placeholder="Filter by education"
                                            value={filters.education}
                                            onChange={handleFilterChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                        <input
                                            type="text"
                                            name="company"
                                            placeholder="Filter by company"
                                            value={filters.company}
                                            onChange={handleFilterChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                        <input
                                            type="text"
                                            name="license"
                                            placeholder="Filter by license"
                                            value={filters.license}
                                            onChange={handleFilterChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                        <input
                                            type="text"
                                            name="location"
                                            placeholder="Filter by location"
                                            value={filters.location}
                                            onChange={handleFilterChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredCandidates.map((candidate) => (
                                <div key={candidate.email} className="bg-white p-6 rounded-xl shadow-md">
                                    <div className="flex items-center mb-4">
                                        <img
                                            src={`${API_BASE_URL}/profile/${candidate.email}/picture`}
                                            alt={`${candidate.firstName} ${candidate.lastName}`}
                                            className="w-16 h-16 rounded-full object-cover mr-4"
                                            onError={(e) => {
                                                e.target.src = '/api/placeholder/64/64';
                                            }}
                                        />
                                        <div>
                                            <h2 className="text-xl font-semibold">
                                                {candidate.firstName} {candidate.lastName}
                                            </h2>
                                            <p className="text-gray-600">{candidate.location}</p>
                                        </div>
                                    </div>

                                    {candidate.phoneCode && candidate.phoneNumber && (
                                        <button
                                            onClick={() => handleCall(candidate.phoneCode, candidate.phoneNumber)}
                                            className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                                        >
                                            <Phone className="w-4 h-4" />
                                            {candidate.phoneCode} {candidate.phoneNumber}
                                        </button>
                                    )}

                                    <div className="mb-4">
                                        <h3 className="font-medium mb-2">Skills</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {candidate.skills.map((skill, index) => (
                                                <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                                    {skill}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <h3 className="font-medium mb-2">Languages</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {candidate.languages.map((language, index) => (
                                                <span key={index} className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                                                    {language}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <h3 className="font-medium mb-2">Education</h3>
                                        {candidate.education.map((edu, index) => (
                                            <div key={index} className="text-sm text-gray-600">
                                                <p>{edu.degreeProgram}</p>
                                                <p>{edu.instituteName}</p>
                                                <p className="text-gray-500">{edu.startDate} - {edu.endDate}</p>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => downloadResume(candidate.email)}
                                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                                    >
                                        <Download className="w-4 h-4" />
                                        Download Resume
                                    </button>
                                </div>
                            ))}
                        </div>
                        </div>
            </main>
            <Footer />
        </div>
        </>
    );
};


export default CandidatesPage;